import { useMemo } from "react";
import { objectTypes, actionNames } from "@newsela/snitch";
import { useSearchPageContext } from "@/search/NewSearchPageMVP/provider/SearchPageProvider";
import { useStandardsSearchContext } from "@/search/NewSearchPageMVP/provider/StandardsSearchProvider";
import { AgathaSchemaParamsEnum } from "@/search/NewSearchPageMVP/types/IParametersSchema";
import { STANDARDS_SET_MODE } from "@/search/NewSearchPageMVP/types/IStandardsSetMode";
import { useStandardsModalContext } from "@/search/NewSearchPageMVP/provider/StandardsModalProvider";

interface TrackingProperties {
  [key: string]: string;
}

export const useStandardsModalFooter = (
  setStandardsModalActive: (val: boolean) => void,
) => {
  const { getTemporarySelectedFilters } = useStandardsSearchContext();
  const {
    handlers: { setSearchParamValue },
  } = useSearchPageContext();
  const {
    getters: {
      standardsSetMode,
      newStandardsSet: { title: newTitle, children: newStandardsInSet },
      isEditingSet,
      isConfirmActionModalOpen,
      draftStandardsSetId,
      isPublishingStandardsSet,
    },
  } = useStandardsModalContext();

  const handleApplyFilters = () => {
    const selectedStandards = getTemporarySelectedFilters();
    const standardIdsCommaSeparated = selectedStandards
      .map((val) => val.content_id)
      .join(",");
    setSearchParamValue(
      AgathaSchemaParamsEnum.related_to_standards,
      standardIdsCommaSeparated,
    );
    setStandardsModalActive(false);
  };

  const getConfirmButtonText = () => {
    if (standardsSetMode === STANDARDS_SET_MODE.CREATE) {
      return "Create standard set";
    } else if (standardsSetMode === STANDARDS_SET_MODE.EDIT) {
      return "Update set";
    } else {
      return "Apply filters";
    }
  };

  const confirmButtonText = getConfirmButtonText();

  const dataQaSelectors = {
    cancel:
      standardsSetMode === STANDARDS_SET_MODE.CREATE
        ? "cancel-new-standards-set-filter-button"
        : "cancel-standards-filter-button",
    confirm:
      standardsSetMode === STANDARDS_SET_MODE.CREATE
        ? "create-new-standards-set-button"
        : "apply-standards-filter-button",
  };

  const trackingProperties: TrackingProperties = {
    [STANDARDS_SET_MODE.CREATE]: "Create new standards set",
    [STANDARDS_SET_MODE.EDIT]: "Edit standard set",
    [STANDARDS_SET_MODE.VIEW]: "Apply standards to apply to search query",
  };

  const getCancelEventProperties = () => ({
    objectType: objectTypes.content,
    actionName: actionNames.cancel,
    streamProperties: {
      description: "Cancel standards search filtering",
    },
  });

  const getEventProperties = (standardsSetMode: string) => {
    return {
      objectType: objectTypes.content,
      actionName: actionNames.create,
      streamProperties: {
        description: trackingProperties[standardsSetMode] || "",
      },
    };
  };

  const isConfirmButtonDisabled = useMemo(
    () => ({
      [STANDARDS_SET_MODE.CREATE]:
        newTitle === "" ||
        newStandardsInSet.length === 0 ||
        draftStandardsSetId === "",
      [STANDARDS_SET_MODE.DELETE]: isEditingSet || isConfirmActionModalOpen,
      [STANDARDS_SET_MODE.EDIT]: false,
      [STANDARDS_SET_MODE.VIEW]: false,
    }),
    [newTitle, newStandardsInSet, isEditingSet, isConfirmActionModalOpen],
  );

  const confirmButtonLoading = useMemo(
    () => ({
      [STANDARDS_SET_MODE.CREATE]: isPublishingStandardsSet,
      [STANDARDS_SET_MODE.EDIT]: false,
      [STANDARDS_SET_MODE.DELETE]: false,
      [STANDARDS_SET_MODE.VIEW]: false,
    }),
    [isPublishingStandardsSet],
  );

  return {
    dataQaSelectors,
    getCancelEventProperties,
    getEventProperties,
    confirmButtonText,
    handleApplyFilters,
    isConfirmButtonDisabled,
    confirmButtonLoading,
  };
};
